import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Search, Check, ArrowRight } from "lucide-react";
import { LANGUAGES } from "@/languages";

const LOGO = "https://customer-assets.emergentagent.com/job_77e99736-a114-44cf-bdab-fa0372b7c2a2/artifacts/nx8mcx1b_Zaynar%20Systems%20logo.png.png";

export default function LanguageGate({ onSelect }) {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(null);

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return LANGUAGES;
    return LANGUAGES.filter(
      (l) => l.name.toLowerCase().includes(q) || l.native.toLowerCase().includes(q) || l.code.includes(q)
    );
  }, [query]);

  const choose = (l) => {
    setActive(l.code);
    setTimeout(() => onSelect(l), 220);
  };

  return (
    <div className="min-h-screen bg-[#050505] zs-grain text-white flex items-center justify-center px-5 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: "easeOut" }}
        className="w-full max-w-3xl"
      >
        <div className="text-center">
          <img src={LOGO} alt="Zaynar Systems" className="w-20 h-20 mx-auto object-contain mb-6" />
          <span className="text-xs font-sans uppercase tracking-[0.25em] text-gold">Zaynar Systems</span>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl font-light tracking-tight">
            Select your <span className="zs-gold-text">language</span>
          </h1>
          <p className="mt-3 font-sans text-sm text-[#A3A3A3]">
            Selecciona tu idioma · Choisissez votre langue · اختر لغتك
          </p>
        </div>

        <div className="mt-8 relative">
          <Search className="w-4 h-4 text-[#777] absolute left-4 top-1/2 -translate-y-1/2" />
          <input
            data-testid="language-search-input"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search language…  ·  Buscar idioma…"
            className="w-full bg-[#0d0d0d] border border-[#262626] rounded-xl text-white pl-11 pr-4 py-3.5 font-sans focus:outline-none focus:border-gold transition-colors placeholder-[#666]"
          />
        </div>

        <div className="mt-6 max-h-[46vh] overflow-y-auto pr-1 grid sm:grid-cols-2 gap-2.5">
          {list.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => choose(l)}
              data-testid={`language-option-${l.code}`}
              className={`group flex items-center justify-between gap-3 text-left rounded-xl px-4 py-3 border transition-all duration-200 ${
                active === l.code
                  ? "bg-[#1A1A14] border-gold"
                  : "bg-[#0d0d0d] border-[#1f1f1f] hover:border-gold/50 hover:bg-[#121212]"
              }`}
            >
              <span className="min-w-0">
                <span className="block font-sans text-white truncate">{l.native}</span>
                <span className="block font-sans text-xs text-[#777] truncate">{l.name}</span>
              </span>
              <span className="flex items-center gap-2 shrink-0">
                {active === l.code
                  ? <Check className="w-4 h-4 text-gold" />
                  : <ArrowRight className="w-4 h-4 text-[#555] group-hover:text-gold transition-colors" />}
              </span>
            </button>
          ))}
          {list.length === 0 && (
            <p className="col-span-full text-center py-8 font-sans text-sm text-[#777]">No language found.</p>
          )}
        </div>

        <p className="mt-6 text-center font-sans text-xs text-[#555] leading-relaxed">
          The full contract &amp; signed PDF are presented in the language you choose.
        </p>
      </motion.div>
    </div>
  );
}
